import type { Gate, Vec2 } from '@sw/schema'
import { sub } from './vec.js'

export type CrossingDirection = 'forward' | 'backward'

/** Which side of the directed line a→b a point falls on. Positive is left. */
function side(a: Vec2, b: Vec2, p: Vec2): number {
  const ab = sub(b, a)
  const ap = sub(p, a)
  return ab.x * ap.y - ab.y * ap.x
}

/**
 * Detects the listener stepping over a line between two consecutive positions,
 * and which way.
 *
 * Direction matters: a start line should begin the race when it is crossed on
 * the way out and stay quiet when the runner wanders back over it during a
 * warm-up. `forward` means crossing from the right-hand side of a→b to the
 * left.
 *
 * Returns undefined when the move does not cross the gate at all.
 */
export function detectGateCrossing(
  gate: Gate,
  from: Vec2,
  to: Vec2,
): CrossingDirection | undefined {
  // A point exactly on the line counts as being on the left. Assigning the
  // boundary to one side rather than treating it as neither means a fix that
  // lands precisely on the gate — which a simulated or gridded position does
  // often — still resolves, and the crossing is counted once instead of being
  // lost between the two steps that straddle it.
  const startedLeft = side(gate.a, gate.b, from) >= 0
  const endedLeft = side(gate.a, gate.b, to) >= 0
  if (startedLeft === endedLeft) return undefined

  // The movement must pass between the gate's endpoints, not around them.
  const endpointALeft = side(from, to, gate.a) >= 0
  const endpointBLeft = side(from, to, gate.b) >= 0
  if (endpointALeft === endpointBLeft) return undefined

  return startedLeft ? 'backward' : 'forward'
}
